import { useEffect, useState } from "react";
import { useSearch, useLocation } from "wouter";
import { MessageSquare, Send, Loader2, ChevronLeft } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

type Compose = { toUserId: number; contextType?: string; contextId?: string; subject?: string; toName?: string };

export default function Messages() {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const search = useSearch();

  const [threads, setThreads] = useState<any[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [convo, setConvo] = useState<{ thread: any; messages: any[] } | null>(null);
  const [compose, setCompose] = useState<Compose | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (threads.length && !activeId && !compose && typeof window !== "undefined" && window.innerWidth >= 1024) setActiveId(threads[0].id);
  }, [threads]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  async function loadInbox() {
    try { const r = await api.listThreads(); setThreads(r?.threads ?? []); } catch { /* keep */ }
  }
  useEffect(() => { if (user) loadInbox(); /* eslint-disable-next-line */ }, [user?.id]);

  // ?to=<id>&type=<ctx>&id=<ctxId>&name=<label>&intent=<hire|quote> opens a compose pane
  useEffect(() => {
    const sp = new URLSearchParams(search);
    const to = Number(sp.get("to"));
    if (to > 0) {
      const intent = sp.get("intent") || "";
      const name = sp.get("name") || undefined;
      const subject = sp.get("subject") || (intent === "hire" ? "Hiring inquiry" : intent === "quote" ? "Quote request" : undefined);
      setCompose({ toUserId: to, contextType: sp.get("type") || "direct", contextId: sp.get("id") || "", subject, toName: name });
      // helpful starter message for hire/quote intents
      if (intent === "hire") setDraft(`Hi${name ? " " + name : ""}, I'd like to hire you for a job. Are you available to discuss the details?`);
      else if (intent === "quote") setDraft(`Hi${name ? " " + name : ""}, could you provide a quote for the following?`);
      setActiveId(null); setConvo(null);
    }
  }, [search]);

  async function openThread(id: string) {
    setActiveId(id); setCompose(null); setConvo(null);
    try { setConvo(await api.getThread(id)); } catch { setErr("Could not open conversation."); }
  }

  async function sendInThread(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim(); if (!body || !activeId) return;
    setBusy(true); setErr(null);
    try {
      await api.postMessage(activeId, body); setDraft("");
      setConvo(await api.getThread(activeId)); await loadInbox();
    } catch (e: any) { setErr(e?.message || "Could not send."); } finally { setBusy(false); }
  }

  async function sendCompose(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim(); if (!body || !compose) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.startThread({ toUserId: compose.toUserId, contextType: compose.contextType, contextId: compose.contextId, subject: compose.subject, body });
      setDraft("");
      await loadInbox();
      if (r?.threadId) { setCompose(null); openThread(r.threadId); }
    } catch (e: any) { setErr(e?.message || "Could not send message."); } finally { setBusy(false); }
  }

  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  return (
    <div className="container-x py-6">
      {/* QA Tab3 #9: smaller heading on mobile so it doesn't dominate the screen. */}
      <h1 className="text-lg sm:text-2xl font-bold sm:font-extrabold text-navy mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />Messages</h1>
      <div className="grid lg:grid-cols-[300px_1fr] gap-4">
        {/* Inbox list */}
        <aside className={`card p-2 h-fit ${(activeId || compose) ? "hidden lg:block" : ""}`}>
          {threads.length === 0 ? (
            <p className="text-sm text-mut p-4 text-center">No conversations yet. Message a project owner or supplier to start one.</p>
          ) : threads.map((t) => (
            <button key={t.id} onClick={() => openThread(t.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg hover:bg-navy-50 ${activeId === t.id ? "bg-navy-50" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-navy text-sm truncate">{t.otherName}</span>
                {t.contextType !== "direct" && <span className="chip bg-navy-50 text-navy-600 text-[10px] shrink-0">{t.contextType}</span>}
              </div>
              <div className="text-xs text-mut truncate">{t.lastFromMe ? "You: " : ""}{t.lastMessage}</div>
            </button>
          ))}
        </aside>

        {/* Conversation / compose — QA Tab3 #9: on mobile, hide this panel entirely when
            nothing is selected so the big "Select a conversation" card doesn't sit under the list. */}
        <div className={`card p-0 min-h-[420px] flex-col ${(activeId || compose) ? "flex" : "hidden lg:flex"}`}>
          {!activeId && !compose ? (
            <div className="flex-1 grid place-items-center text-mut text-sm p-8 text-center">
              <div><MessageSquare className="w-8 h-8 mx-auto mb-2 text-navy-500" />Select a conversation to read it here.</div>
            </div>
          ) : compose ? (
            <>
              <div className="px-4 py-3 border-b border-line flex items-center gap-2">
                <button onClick={() => setCompose(null)} className="lg:hidden text-mut"><ChevronLeft className="w-5 h-5" /></button>
                <div className="font-bold text-navy">New message{compose.toName ? ` · ${compose.toName}` : ""}</div>
                {compose.contextType && compose.contextType !== "direct" && <span className="chip bg-navy-50 text-navy-600 text-[10px]">{compose.contextType}</span>}
              </div>
              <div className="flex-1 grid place-items-center text-mut text-sm p-8">Write your first message below.</div>
              {err && <div className="px-4 text-xs text-brand-red">{err}</div>}
              <form onSubmit={sendCompose} className="p-3 border-t border-line flex gap-2">
                <input className="field" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type a message…" autoFocus />
                <button disabled={busy} className="btn-red px-3">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
              </form>
            </>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-line flex items-center gap-2">
                <button onClick={() => { setActiveId(null); setConvo(null); }} className="lg:hidden text-mut"><ChevronLeft className="w-5 h-5" /></button>
                <div className="font-bold text-navy">{convo?.thread?.otherName || "…"}</div>
                {convo?.thread?.contextType && convo.thread.contextType !== "direct" && <span className="chip bg-navy-50 text-navy-600 text-[10px]">{convo.thread.contextType}</span>}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[52vh]">
                {!convo ? <div className="text-center text-mut text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> loading…</div>
                  : convo.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.mine ? "bg-navy text-white" : "bg-navy-50 text-navy"}`}>{m.body}</div>
                    </div>
                  ))}
              </div>
              {err && <div className="px-4 text-xs text-brand-red">{err}</div>}
              <form onSubmit={sendInThread} className="p-3 border-t border-line flex gap-2">
                <input className="field" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type a message…" />
                <button disabled={busy} className="btn-red px-3">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
